import { useState, useCallback, useRef } from 'react';
import api from '../api/api';

/**
 * useSurvey — manages survey trigger evaluation and pending state.
 *
 * Usage:
 *   const { pendingSurvey, checkSurveys, dismissSurvey, completeSurvey } = useSurvey();
 *
 *   // Trigger check on page load:
 *   useEffect(() => { checkSurveys('signup'); }, []);
 *
 *   // Render modal when a survey is pending:
 *   {pendingSurvey && (
 *     <SurveyModal
 *       surveyData={pendingSurvey}
 *       onComplete={completeSurvey}
 *       onDismiss={() => dismissSurvey(pendingSurvey.SurveyID)}
 *     />
 *   )}
 *
 * Context values:
 *   'signup'          — after user completes registration
 *   'dashboard'       — on dashboard load (evaluates login_count and days triggers)
 *   'progress_report' — when the Progress Report page is viewed
 *   'manual'          — triggered via direct link (?survey=manual)
 */
export function useSurvey() {
    const [pendingSurvey, setPendingSurvey] = useState(null);

    // Session-dismissed IDs — only persists for current browser session.
    // The permanent completion gate is the DB (SurveyCompletions table).
    // Dismissed surveys re-appear on the next session until fully completed.
    const sessionDismissed = useRef(new Set());

    // Prevent concurrent calls from firing multiple times
    const checking = useRef(false);

    const checkSurveys = useCallback(async (context) => {
        if (checking.current) return;
        checking.current = true;
        try {
            const res = await api.get('/survey/pending', {
                params: { context },
            });
            const surveys = res.data?.surveys || [];

            // Show the first survey not dismissed this session
            const next = surveys.find(s => !sessionDismissed.current.has(s.SurveyID));
            if (next) {
                setPendingSurvey(next);
            }
        } catch (err) {
            // Surveys are non-critical — fail silently
            console.debug('useSurvey: could not fetch pending surveys', err);
        } finally {
            checking.current = false;
        }
    }, []);

    /** Close without completing — dismissed only for this session */
    const dismissSurvey = useCallback((surveyId) => {
        sessionDismissed.current.add(surveyId);
        setPendingSurvey(null);
    }, []);

    /** Called after SurveyModal successfully submitted — clears local state */
    const completeSurvey = useCallback(() => {
        setPendingSurvey(null);
    }, []);

    return { pendingSurvey, checkSurveys, dismissSurvey, completeSurvey };
}
